/**
 * Minimal Markdown → HTML for EAD skill report view (ported from EAD-PFM Editor).
 */

export function markdownToHtml(md: string): string {
  if (!md?.trim()) return "";
  const lines = md.split("\n");
  const out: string[] = [];
  let inList = false;
  let inOrderedList = false;
  let inCodeBlock = false;
  let codeLanguage = "";
  const codeLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    const fenceMatch = trimmed.match(/^```(\w*)$/);
    if (fenceMatch) {
      if (inCodeBlock) {
        out.push(renderCodeBlock(codeLines.join("\n"), codeLanguage));
        codeLines.length = 0;
        codeLanguage = "";
        inCodeBlock = false;
      } else {
        if (inList) {
          out.push("</ul>");
          inList = false;
        }
        inCodeBlock = true;
        codeLanguage = fenceMatch[1]?.toLowerCase() || "";
      }
      continue;
    }
    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }

    if (trimmed.startsWith("### ")) {
      if (inList) {
        out.push("</ul>");
        inList = false;
      }
      out.push(`<h3 class="help-tip-h3">${inlinify(trimmed.slice(4))}</h3>`);
      continue;
    }
    if (trimmed.startsWith("## ")) {
      if (inList) {
        out.push("</ul>");
        inList = false;
      }
      out.push(`<h2 class="help-tip-h2">${inlinify(trimmed.slice(3))}</h2>`);
      continue;
    }
    if (trimmed.startsWith("# ")) {
      if (inList) {
        out.push("</ul>");
        inList = false;
      }
      out.push(`<h1 class="help-tip-h1">${inlinify(trimmed.slice(2))}</h1>`);
      continue;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (orderedMatch) {
      if (inList) {
        out.push("</ul>");
        inList = false;
      }
      if (!inOrderedList) {
        out.push('<ol class="help-tip-ol">');
        inOrderedList = true;
      }
      out.push(`<li class="help-tip-li">${inlinify(orderedMatch[1]!)}</li>`);
      continue;
    }

    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      if (inOrderedList) {
        out.push("</ol>");
        inOrderedList = false;
      }
      if (!inList) {
        out.push('<ul class="help-tip-ul">');
        inList = true;
      }
      out.push(`<li class="help-tip-li">${inlinify(trimmed.slice(2))}</li>`);
      continue;
    }

    if (/^[-*_]{3,}$/.test(trimmed)) {
      if (inList) {
        out.push("</ul>");
        inList = false;
      }
      out.push('<hr class="help-tip-hr" />');
      continue;
    }

    if (trimmed === "") {
      if (inList) {
        out.push("</ul>");
        inList = false;
      }
      continue;
    }

    if (inList) {
      out.push("</ul>");
      inList = false;
    }
    if (inOrderedList) {
      out.push("</ol>");
      inOrderedList = false;
    }
    out.push(`<p class="help-tip-p">${inlinify(trimmed)}</p>`);
  }

  if (inList) out.push("</ul>");
  if (inOrderedList) out.push("</ol>");
  if (inCodeBlock) {
    out.push(renderCodeBlock(codeLines.join("\n"), codeLanguage));
  }
  return out.join("");
}

function renderCodeBlock(code: string, language: string): string {
  if (language === "mermaid") {
    return `<div class="markdown-mermaid-block" data-mermaid="${encodeURIComponent(code)}"><pre class="help-tip-code markdown-mermaid-source"><code>${escapeHtml(code)}</code></pre></div>`;
  }
  const langClass = language ? ` language-${escapeHtml(language)}` : "";
  return `<pre class="help-tip-code${langClass}"><code>${escapeHtml(code)}</code></pre>`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inlinify(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, '<code class="help-tip-code">$1</code>')
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener" class="help-tip-link">$1</a>',
    );
}
